/**
 * schoolAssessmentListAdapter — maps `GET school-assessments` rows → `UISchoolAssessment`.
 *
 * API-aware (`chapters_progress`, nested `school_subject`). Not imported by the DS.
 */
import { type UISchoolAssessment } from "../../../ui/types"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

type ApiChapterProgress = {
    chapter_id?: string | number
    name?: string
    progress?: number
}

/** Loose school-assessment row from `schoolAssessments.byStudentId`. */
export type ApiSchoolAssessment = {
    id?: string | number
    title?: string | null
    date?: string
    urgency?: string
    key_requirements?: string | null
    preparation_progress?: number
    school_subject?: ApiSchoolSubject | null
    chapters_progress?: ApiChapterProgress[] | null
}

const URGENCIES = new Set(["low", "medium", "high", "critical"])

export function toUISchoolAssessment(raw: ApiSchoolAssessment | null | undefined): UISchoolAssessment | null {
    if (raw?.id == null || !raw.date) return null

    const subject = toUISubject(raw.school_subject)
    const subjectName = subject.name
    const urgencyRaw = raw.urgency ?? "medium"
    const urgency = (URGENCIES.has(urgencyRaw) ? urgencyRaw : "medium") as UISchoolAssessment["urgency"]

    const chaptersProgress = (raw.chapters_progress ?? [])
        .map((row) => {
            if (row?.chapter_id == null || !row.name) return null
            return {
                chapter: {
                    id: row.chapter_id,
                    name: row.name,
                    subject,
                },
                progress: row.progress ?? 0,
            }
        })
        .filter((row): row is NonNullable<typeof row> => row != null)

    // When the API only has overall preparation_progress, feed one synthetic row so the DS
    // average matches the legacy card % (same trick as linked assessments on the report).
    if (chaptersProgress.length === 0 && typeof raw.preparation_progress === "number") {
        chaptersProgress.push({
            chapter: { id: raw.id, name: raw.title || `Contrôle ${subjectName}`, subject },
            progress: raw.preparation_progress,
        })
    }

    return {
        id: raw.id,
        title: raw.title || `Contrôle ${subjectName}`,
        date: raw.date,
        subject,
        chaptersProgress,
        urgency,
        keyRequirements: raw.key_requirements || undefined,
    }
}

export function toUISchoolAssessments(rawList: ApiSchoolAssessment[] | null | undefined): UISchoolAssessment[] {
    return (rawList ?? [])
        .map((row) => toUISchoolAssessment(row))
        .filter((row): row is UISchoolAssessment => row != null)
}
